import React, {
  useEffect,
  useState,
  useMemo
} from 'react';

import {
  useParams,
  useNavigate
} from 'react-router-dom';

import {
  FaArrowLeft,
  FaWallet,
  FaChartLine,
  FaUsers,
  FaMoneyBillWave,
  FaCoins,
  FaTrophy,
  FaPlus,
  FaMapMarkerAlt,
  FaUserCircle,
  FaEdit
} from 'react-icons/fa';

import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import TopupModal from '../components/TopupModal';
import EditModal from '../components/EditModal';   // ← Corrected Name

import {
  topupUser,
  getSingleUser
} from '../services/userService';

import './UserDetails.css';

// Fix Leaflet Default Marker Icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
});

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */
  const [userDetails, setUserDetails] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [withdraws, setWithdraws] = useState([]);
  const [stakes, setStakes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);   // ← Separate State for Edit Modal

  const [activeLocation, setActiveLocation] = useState('login');

  /*
  |--------------------------------------------------------------------------
  | PARSE LOCATION COORDINATES
  |--------------------------------------------------------------------------
  */
  const locationCoordinates = useMemo(() => {
    let oldLocation = null;
    let loginLocation = null;

    // Saved Location
    if (userDetails?.location) {
      const coords = userDetails.location.split(',').map(coord => parseFloat(coord.trim()));
      if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        oldLocation = { lat: coords[0], lng: coords[1] };
      }
    }

    // Login Location
    if (userDetails?.login_location) {
      try {
        const parsed = JSON.parse(userDetails.login_location);
        if (parsed.latitude && parsed.longitude) {
          loginLocation = {
            lat: parseFloat(parsed.latitude),
            lng: parseFloat(parsed.longitude)
          };
        }
      } catch (err) {
        console.log('Login location parse error:', err);
      }
    }

    return { oldLocation, loginLocation };
  }, [userDetails]);

  /*
  |--------------------------------------------------------------------------
  | FETCH USER DETAILS
  |--------------------------------------------------------------------------
  */
  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await getSingleUser(userId);

      if (response.status) {
        setUserDetails(response.data);
        setDeposits(response.deposits || []);
        setWithdraws(response.withdraws || []);
        setStakes(response.stakes || []);
      }
    } catch (error) {
      console.error('User Details Error:', error);
      toast.error(error.message || 'Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  /*
  |--------------------------------------------------------------------------
  | HANDLE TOPUP
  |--------------------------------------------------------------------------
  */
  const handleTopup = async (amount) => {
    try {
      const response = await topupUser(userDetails.id, amount);
      if (response.status) {
        toast.success(`Successfully added $${amount} to user account`);
        fetchUserDetails();
        setShowTopupModal(false);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process topup');
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING & ERROR
  |--------------------------------------------------------------------------
  */
  if (loading) {
    return <div className="dashboard-loading">Loading user details...</div>;
  }

  if (!userDetails) {
    return <div className="user-details-error">User not found</div>;
  }

  return (
    <div className="user-details-page">

      {/* HEADER */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-top">
            <button className="back-button" onClick={() => navigate('/users')}>
              <FaArrowLeft /> Back to Users
            </button>

            <div className="header-actions">
              <button
                className="topup-button"
                onClick={() => setShowEditModal(true)}
              >
                <FaEdit /> Edit User
              </button>

              <br />
              <br />

              <button
                className="topup-button"
                onClick={() => setShowTopupModal(true)}
              >
                <FaPlus /> Topup Account
              </button>
            </div>
          </div>

          <div className="header-info">
            <div className="user-profile-header">
              <div className="profile-image-container">
                {userDetails.profile_image ? (
                  <img
                    src={userDetails.profile_image}
                    alt={userDetails.name || 'User'}
                    className="profile-image"
                  />
                ) : (
                  <FaUserCircle className="default-profile-icon" />
                )}
              </div>

              <div className="profile-details">
                <h1>{userDetails.name || 'N/A'}</h1>
                <p className="user-id">ID: #{userDetails.id}</p>
                <p className="user-wallet">{userDetails.wallet || 'N/A'}</p>

                <div className="user-badges">
                  <span className={`status-badge status-${userDetails.user_status}`}>
                    {userDetails.user_status}
                  </span>
                  <span className="rank-badge">
                    {userDetails.rank || 'ECONOMY'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div className="stats-grid">

        <div className="stat-card gradient-purple">

          <div className="stat-icon">

            <FaWallet />

          </div>

          <div className="stat-details">

            <h3>
              Deposit Amount
            </h3>

            <p className="stat-value">

              $
              {Number(
                userDetails.deposit_amount || 0
              ).toLocaleString()}

            </p>

          </div>

        </div>

        <div className="stat-card gradient-green">

          <div className="stat-icon">

            <FaMoneyBillWave />

          </div>

          <div className="stat-details">

            <h3>
              Total Income
            </h3>

            <p className="stat-value">

              $
              {Number(
                userDetails.total_income || 0
              ).toLocaleString()}

            </p>

          </div>

        </div>

        <div className="stat-card gradient-blue">

          <div className="stat-icon">

            <FaCoins />

          </div>

          <div className="stat-details">

            <h3>
              Available Amount
            </h3>

            <p className="stat-value">

              $
              {Number(
                userDetails.available_amount || 0
              ).toLocaleString()}

            </p>

          </div>

        </div>

        <div className="stat-card gradient-orange">

          <div className="stat-icon">

            <FaUsers />

          </div>

          <div className="stat-details">

            <h3>
              Total Team
            </h3>

            <p className="stat-value">

              {userDetails.total_team || 0}

            </p>

          </div>

        </div>

      </div>

      {/* DETAILS */}

      <div className="details-container">

        {/* BASIC INFO */}

        <div className="detail-card">

          <div className="card-header">

            <h2>

              <FaWallet />

              Basic Information

            </h2>

          </div>

          <div className="card-body">

            <div className="info-grid">

              <div className="info-item">

                <label>
                  Name
                </label>

                <span>
                  {userDetails.name || 'N/A'}
                </span>

              </div>

              <div className="info-item">

                <label>
                  Wallet Address
                </label>

                <span className="wallet-address">

                  {userDetails.wallet || 'N/A'}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Location
                </label>

                <span>

                  {userDetails.fix_address || 'N/A'}

                </span>

              </div>

              <div className="info-item">

                <label>
                  User Status
                </label>

                <span
                  className={`status-badge status-${userDetails.user_status}`}
                >

                  {userDetails.user_status}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Profile Status
                </label>

                <span>

                  {userDetails.profile_status}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Joined Date
                </label>

                <span>

                  {userDetails.date_time}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Sponsor Wallet
                </label>

                <span className="wallet-address">

                  {userDetails.sponsor_id ?
                    `ID: ${userDetails.sponsor_id}` : 'N/A'}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Login Status
                </label>

                <span className={`status-badge status-${userDetails.login_status === 'login' ? 'active' : 'pending'}`}>

                  {userDetails.login_status || 'N/A'}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Last Login
                </label>

                <span>

                  {userDetails.last_login || 'Never'}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Stake Status
                </label>

                <span className={`status-badge status-${userDetails.stake_status === 'active' ? 'active' : 'pending'}`}>

                  {userDetails.stake_status || 'pending'}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Withdraw Status
                </label>

                <span className={`status-badge status-${userDetails.withdraw_status === 'enable' ? 'active' : 'blocked'}`}>

                  {userDetails.withdraw_status || 'N/A'}

                </span>

              </div>

            </div>

          </div>

        </div>

        {/* LOCATION MAP */}

        {/* LOCATION SECTION */}

        <div className="location-grid">

          {/* SAVED LOCATION */}

          {/* LOCATION SECTION */}

          {(locationCoordinates?.oldLocation ||
            locationCoordinates?.loginLocation) && (

              <div className="detail-card">

                <div className="card-header location-header">

                  <h2>

                    <FaMapMarkerAlt />

                    User Location

                  </h2>

                  <div className="location-switch-buttons">

                    {locationCoordinates?.loginLocation && (

                      <button
                        className={`location-switch-btn ${activeLocation === 'login'
                          ? 'active'
                          : ''
                          }`}
                        onClick={() =>
                          setActiveLocation('login')
                        }
                      >

                        Last Login

                      </button>

                    )}

                    {locationCoordinates?.oldLocation && (

                      <button
                        className={`location-switch-btn ${activeLocation === 'saved'
                          ? 'active'
                          : ''
                          }`}
                        onClick={() =>
                          setActiveLocation('saved')
                        }
                      >

                        Original Location

                      </button>

                    )}

                  </div>

                </div>

                <div className="card-body">

                  {/* LOGIN LOCATION */}

                  {activeLocation === 'login' &&
                    locationCoordinates?.loginLocation && (

                      <>

                        <div className="location-info">

                          <div className="info-item">

                            <label>
                              Latitude
                            </label>

                            <span>
                              {locationCoordinates.loginLocation.lat}
                            </span>

                          </div>

                          <div className="info-item">

                            <label>
                              Longitude
                            </label>

                            <span>
                              {locationCoordinates.loginLocation.lng}
                            </span>

                          </div>

                          <div className="info-item">

                            <label>
                              Last Login
                            </label>

                            <span>
                              {userDetails.last_login || 'N/A'}
                            </span>

                          </div>

                        </div>

                        <div className="map-container">

                          <MapContainer
                            center={[
                              locationCoordinates.loginLocation.lat,
                              locationCoordinates.loginLocation.lng
                            ]}
                            zoom={15}
                            style={{
                              height: '350px',
                              width: '100%',
                              borderRadius: '12px'
                            }}
                          >

                            <TileLayer
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              attribution='&copy; OpenStreetMap contributors'
                            />

                            <Marker
                              position={[
                                locationCoordinates.loginLocation.lat,
                                locationCoordinates.loginLocation.lng
                              ]}
                            >

                              <Popup>

                                Current Login Location

                              </Popup>

                            </Marker>

                          </MapContainer>

                        </div>

                      </>

                    )}

                  {/* SAVED LOCATION */}

                  {activeLocation === 'saved' &&
                    locationCoordinates?.oldLocation && (

                      <>

                        <div className="location-info">

                          <div className="info-item">

                            <label>
                              Latitude
                            </label>

                            <span>
                              {locationCoordinates.oldLocation.lat}
                            </span>

                          </div>

                          <div className="info-item">

                            <label>
                              Longitude
                            </label>

                            <span>
                              {locationCoordinates.oldLocation.lng}
                            </span>

                          </div>

                          <div className="info-item">

                            <label>
                              Address
                            </label>

                            <span>
                              {userDetails.fix_address || 'N/A'}
                            </span>

                          </div>

                        </div>

                        <div className="map-container">

                          <MapContainer
                            center={[
                              locationCoordinates.oldLocation.lat,
                              locationCoordinates.oldLocation.lng
                            ]}
                            zoom={15}
                            style={{
                              height: '350px',
                              width: '100%',
                              borderRadius: '12px'
                            }}
                          >

                            <TileLayer
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              attribution='&copy; OpenStreetMap contributors'
                            />

                            <Marker
                              position={[
                                locationCoordinates.oldLocation.lat,
                                locationCoordinates.oldLocation.lng
                              ]}
                            >

                              <Popup>

                                Original Saved Location

                              </Popup>

                            </Marker>

                          </MapContainer>

                        </div>

                      </>

                    )}

                </div>

              </div>

            )}

        </div>

        {/* FINANCIAL */}

        <div className="detail-card">

          <div className="card-header">

            <h2>

              <FaMoneyBillWave />

              Financial Information

            </h2>

          </div>

          <div className="card-body">

            <div className="info-grid">

              <div className="info-item">

                <label>
                  Deposit Amount
                </label>

                <span className="amount-value">

                  $
                  {Number(
                    userDetails.deposit_amount || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Available Deposit
                </label>

                <span className="amount-value success">

                  $
                  {Number(
                    userDetails.available_deposit || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Withdraw Amount
                </label>

                <span className="amount-value warning">

                  $
                  {Number(
                    userDetails.withdraw_amount || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Available Amount
                </label>

                <span className="amount-value primary">

                  $
                  {Number(
                    userDetails.available_amount || 0
                  ).toLocaleString()}

                </span>

              </div>

            </div>

          </div>

        </div>

        {/* TEAM INFORMATION */}

        <div className="detail-card">

          <div className="card-header">

            <h2>

              <FaUsers />

              Team & Referral Information

            </h2>

          </div>

          <div className="card-body">

            <div className="info-grid">

              <div className="info-item">

                <label>
                  My Referrals
                </label>

                <span className="team-value">

                  {Number(
                    userDetails.my_referral || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Active Referrals
                </label>

                <span className="team-value success">

                  {Number(
                    userDetails.active_referral || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Total Team
                </label>

                <span className="team-value primary">

                  {Number(
                    userDetails.total_team || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  My Active Team
                </label>

                <span className="team-value">

                  {Number(
                    userDetails.my_active || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Active Level
                </label>

                <span className="team-value">

                  Level {userDetails.active_level || 0}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Trade Amount
                </label>

                <span className="amount-value">

                  $
                  {Number(
                    userDetails.my_stake || 0
                  ).toLocaleString()}

                </span>

              </div>

            </div>

          </div>

        </div>

        {/* BUSINESS */}

        <div className="detail-card">

          <div className="card-header">

            <h2>

              <FaChartLine />

              Business Information

            </h2>

          </div>

          <div className="card-body">

            <div className="info-grid">

              <div className="info-item">

                <label>
                  My Business
                </label>

                <span>

                  $
                  {Number(
                    userDetails.my_business || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Direct Business
                </label>

                <span>

                  $
                  {Number(
                    userDetails.direct_business || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Team Business
                </label>

                <span>

                  $
                  {Number(
                    userDetails.team_business || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="info-item">

                <label>
                  Month Business
                </label>

                <span>

                  $
                  {Number(
                    userDetails.month_business || 0
                  ).toLocaleString()}

                </span>

              </div>

            </div>

          </div>

        </div>

        {/* INCOME */}

        <div className="detail-card">

          <div className="card-header">

            <h2>

              <FaTrophy />

              Income Breakdown

            </h2>

          </div>

          <div className="card-body">

            <div className="income-grid">

              <div className="income-item">

                <div className="income-label">

                  Direct Income

                </div>

                <span className="income-value">

                  $
                  {Number(
                    userDetails.direct_income || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="income-item">

                <div className="income-label">

                  Trade Income

                </div>

                <span className="income-value">

                  $
                  {Number(
                    userDetails.stake_income || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="income-item">

                <div className="income-label">

                  Level Income

                </div>

                <span className="income-value">

                  $
                  {Number(
                    userDetails.level_income || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="income-item">

                <div className="income-label">

                  Booster Income

                </div>

                <span className="income-value">

                  $
                  {Number(
                    userDetails.booster_income || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="income-item">

                <div className="income-label">

                  Rank Bonus

                </div>

                <span className="income-value">

                  $
                  {Number(
                    userDetails.rank_bonus || 0
                  ).toLocaleString()}

                </span>

              </div>

              <div className="income-item">

                <div className="income-label">

                  Matching Income

                </div>

                <span className="income-value">

                  $
                  {Number(
                    userDetails.maching_income || 0
                  ).toLocaleString()}

                </span>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* MODALS */}
      <TopupModal
        isOpen={showTopupModal}
        onClose={() => setShowTopupModal(false)}
        userDetails={userDetails}
        onConfirm={handleTopup}
      />

      <EditModal
        isOpen={showEditModal}           // ← Fixed
        onClose={() => setShowEditModal(false)}  // ← Fixed
        userDetails={userDetails}
      // onConfirm={handleEdit}       // Use different handler if needed for Edit
      />

    </div>
  );
};

export default UserDetails;